// SimForge Baseball Equipment System
// Equipment that players can equip for attribute boosts
// Stays with the player when sold on marketplace
// 3 slots: Hat, Glove, Sunglasses
// Upgrade: pay difference (new - old value), old item destroyed

export interface EquipmentItem {
  id: string;
  name: string;
  type: 'hat' | 'glove' | 'sunglasses';
  rarity: 'common' | 'rare' | 'epic';
  cost: number;  // Cost in $BALLS: common=500($5), rare=1000($10), epic=1500($15)
  boost: number; // Attribute boost: common=+3, rare=+5, epic=+8
  description: string;
}

// Available attributes that can be boosted (hitters)
export const EQUIPMENT_ATTRIBUTES = [
  { key: 'power', label: 'Power', icon: '💪' },
  { key: 'contact', label: 'Contact', icon: '🎯' },
  { key: 'speed', label: 'Speed', icon: '⚡' },
  { key: 'fielding', label: 'Fielding', icon: '🧤' },
  { key: 'arm', label: 'Arm', icon: '💪' },
  { key: 'discipline', label: 'Discipline', icon: '🎓' },
  { key: 'endurance', label: 'Endurance', icon: '🏃' },
];

// Pitcher attributes
export const PITCHER_ATTRIBUTES = [
  { key: 'velocity', label: 'Velocity', icon: '🔥' },
  { key: 'control', label: 'Control', icon: '🎯' },
  { key: 'movement', label: 'Movement', icon: '🌊' },
  { key: 'deception', label: 'Deception', icon: '🎭' },
  { key: 'endurance', label: 'Endurance', icon: '🏃' },
];

// Equipment catalog — 3 slots per player (hat, glove, sunglasses)
// Prices: common=$5(500 BALLS), rare=$10(1000 BALLS), epic=$15(1500 BALLS)
// Upgrade: refund old value, pay difference
export const EQUIPMENT_CATALOG: EquipmentItem[] = [
  // Hats
  { id: 'hat_common', name: 'Cap', type: 'hat', rarity: 'common', cost: 500, boost: 3, description: '+3 to one attribute' },
  { id: 'hat_rare', name: 'Snapback', type: 'hat', rarity: 'rare', cost: 1000, boost: 5, description: '+5 to one attribute' },
  { id: 'hat_epic', name: 'Gold Crown', type: 'hat', rarity: 'epic', cost: 1500, boost: 8, description: '+8 to one attribute' },
  
  // Batting Gloves
  { id: 'glove_common', name: 'Batting Gloves', type: 'glove', rarity: 'common', cost: 500, boost: 3, description: '+3 to one attribute' },
  { id: 'glove_rare', name: 'Pro Batting Gloves', type: 'glove', rarity: 'rare', cost: 1000, boost: 5, description: '+5 to one attribute' },
  { id: 'glove_epic', name: 'Gold Batting Gloves', type: 'glove', rarity: 'epic', cost: 1500, boost: 8, description: '+8 to one attribute' },
  
  // Sunglasses
  { id: 'sunglasses_common', name: 'Shades', type: 'sunglasses', rarity: 'common', cost: 500, boost: 3, description: '+3 to one attribute' },
  { id: 'sunglasses_rare', name: 'Aviators', type: 'sunglasses', rarity: 'rare', cost: 1000, boost: 5, description: '+5 to one attribute' },
  { id: 'sunglasses_epic', name: 'Diamond Lenses', type: 'sunglasses', rarity: 'epic', cost: 1500, boost: 8, description: '+8 to one attribute' },
];

// All equipment
export const ALLEquipment = EQUIPMENT_CATALOG;

// Helper to get equipment by ID
export function getEquipmentById(id: string): EquipmentItem | undefined {
  return EQUIPMENT_CATALOG.find(e => e.id === id);
}

// Helper to get equipment by type
export function getEquipmentByType(type: 'hat' | 'glove' | 'sunglasses'): EquipmentItem[] {
  return EQUIPMENT_CATALOG.filter(e => e.type === type);
}

// Helper to get equipment by rarity
export function getEquipmentByRarity(rarity: 'common' | 'rare' | 'epic'): EquipmentItem[] {
  return EQUIPMENT_CATALOG.filter(e => e.rarity === rarity);
}

// Rarity color mapping
export const RARITY_COLORS: Record<string, string> = {
  common: '#9ca3af',
  rare: '#3b82f6',
  epic: '#f59e0b',
};

// Equipment slots
export const EQUIPMENT_SLOTS = ['hat', 'glove', 'sunglasses'] as const;
export type EquipmentSlot = typeof EQUIPMENT_SLOTS[number];

// Equipped item shape (stored in player.equipment JSON)
export interface EquippedItem {
  itemId: string;       // e.g. 'hat_rare'
  attribute: string;    // e.g. 'power'
  boost: number;        // e.g. 5
  rarity: string;       // e.g. 'rare'
}
